import { useState } from 'react';
import { ArrowUpRight, Check, Copy } from 'lucide-react';
import { SITE, SOCIAL } from '../data/site';
import { ABOUT } from '../data/about';
import { trackEvent } from '../utils/analytics';
import { PageSection } from './SectionHeader';

const FORMSPREE_URL = process.env.REACT_APP_FORMSPREE_URL;

function CopyEmail() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SITE.email);
      setCopied(true);
      trackEvent('contact_click', { method: 'copy_email' });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.location.href = `mailto:${SITE.email}`;
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="h-11 w-11 rounded-full border border-line flex items-center justify-center text-muted hover:text-ink hover:border-ink transition-colors"
      aria-label={copied ? 'Email copied' : 'Copy email address'}
    >
      {copied ? <Check size={16} className="text-accent" /> : <Copy size={16} />}
    </button>
  );
}

export default function Contact() {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = new FormData(form);
    const name = data.get('name')?.toString().trim();
    const email = data.get('email')?.toString().trim();
    const message = data.get('message')?.toString().trim();
    const intent = data.get('intent')?.toString();

    if (!name || !email || !message) {
      setError('Please fill in all required fields.');
      setStatus('error');
      return;
    }

    trackEvent('contact_form_submit', { intent });

    if (!FORMSPREE_URL) {
      const subject = encodeURIComponent(`Portfolio contact: ${intent || 'General'}`);
      const body = encodeURIComponent(`Name: ${name}\nEmail: ${email}\nIntent: ${intent}\n\n${message}`);
      window.location.href = `mailto:${SITE.email}?subject=${subject}&body=${body}`;
      setStatus('sent');
      form.reset();
      return;
    }

    setStatus('sending');
    setError('');
    try {
      const res = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message, intent, _replyto: email }),
      });
      if (!res.ok) throw new Error('Failed');
      setStatus('sent');
      form.reset();
    } catch {
      setError('Something went wrong. Please email directly.');
      setStatus('error');
    }
  };

  const links = SOCIAL.filter((s) => s.label !== 'Email');

  return (
    <PageSection id="contact" className="pb-12 lg:pb-16">
      <div className="card overflow-hidden">
        <div className="grid lg:grid-cols-12">
          <div className="lg:col-span-7 p-8 sm:p-12 lg:p-16 border-b lg:border-b-0 lg:border-r border-line">
            <p className="label reveal flex items-center gap-3">
              <span className="relative flex h-2 w-2">
                <span className="absolute inset-0 rounded-full bg-accent animate-pulse-dot" />
                <span className="relative h-2 w-2 rounded-full bg-accent" />
              </span>
              {ABOUT.availability.status}
            </p>
            <h2 className="section-title reveal mt-6" style={{ '--d': '60ms' }}>
              Say <em>hello.</em>
            </h2>
            <p className="reveal mt-6 max-w-md text-base leading-relaxed text-muted" style={{ '--d': '120ms' }}>
              {ABOUT.availability.roles}. {ABOUT.availability.location}. {ABOUT.availability.start}.
              Hiring, a retrieval problem that will not behave, or a founding-team chat. Email is fastest.
            </p>

            <div className="reveal mt-10 flex items-center gap-3 flex-wrap" style={{ '--d': '180ms' }}>
              <a
                href={`mailto:${SITE.email}`}
                className="link text-lg sm:text-2xl font-medium break-all"
                onClick={() => trackEvent('contact_click', { method: 'email' })}
              >
                {SITE.email}
              </a>
              <CopyEmail />
            </div>

            <ul className="reveal mt-10 flex flex-wrap gap-2" style={{ '--d': '240ms' }}>
              {links.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 h-10 text-sm text-muted hover:text-ink hover:border-ink transition-colors"
                    onClick={() => trackEvent('contact_click', { method: s.label })}
                  >
                    {s.label}
                    <ArrowUpRight size={14} aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <form
            onSubmit={handleSubmit}
            className="reveal lg:col-span-5 p-8 sm:p-12 lg:p-12 space-y-4 bg-surface-2/60"
            style={{ '--d': '120ms' }}
            noValidate
          >
            <p className="label">Or use the form</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <input id="name" name="name" type="text" required autoComplete="name" className="field" placeholder="Name" aria-label="Name" />
              <input id="email" name="email" type="email" required autoComplete="email" className="field" placeholder="Email" aria-label="Email" />
            </div>
            <select id="intent" name="intent" defaultValue="Hiring" className="field appearance-none" aria-label="Intent">
              <option>Hiring</option>
              <option>Freelance project</option>
              <option>Research collaboration</option>
              <option>Other</option>
            </select>
            <textarea id="message" name="message" required rows={6} className="field resize-y min-h-[140px]" placeholder="What are you working on?" aria-label="Message" />
            {error && <p className="text-sm text-red-400" role="alert">{error}</p>}
            {status === 'sent' && (
              <p className="text-sm text-accent" role="status">
                {FORMSPREE_URL ? 'Sent. I’ll reply soon.' : 'Opening your email client…'}
              </p>
            )}
            <button type="submit" disabled={status === 'sending'} className="btn-primary w-full justify-center disabled:opacity-50">
              {status === 'sending' ? 'Sending…' : 'Send message'}
            </button>
          </form>
        </div>
      </div>
    </PageSection>
  );
}
